import React, { useState, useEffect } from 'react';
import { Row, Col, Button, message } from 'antd';
import AssignFormModal from './FormModal';
import FormList from './FormList';
import { assetFormService } from '@iso/services';
import IntlMessages from '@iso/components/utility/intlMessages';
import InspectionModal from '@iso/containers/Pages/Form/InspectionModal';
import CreateFormModal from '@iso/containers/Pages/Form/FormModal';
import PreviewFormModal from '@iso/containers/Pages/AdminPages/ManageLibraryForms/PreviewFormModal';

const appendBackLink = (url, backLink) => {
    if (!url || !backLink) {
        return url;
    }
    try {
        const parsed = new URL(url);
        if (!parsed.searchParams.has('back_link')) {
            parsed.searchParams.set('back_link', backLink);
        }
        return parsed.toString();
    } catch (error) {
        if (url.includes('back_link=')) {
            return url;
        }
        const separator = url.includes('?') ? '&' : '?';
        return `${url}${separator}back_link=${encodeURIComponent(backLink)}`;
    }
};

export default function FormTab({ assetId, propertyId }) {
    const [visibleFormModal, setVisibleFormModal] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(10);
    const [total, setTotal] = useState(0);
    const [assetForms, setAssetForms] = useState([]);
    const [mode, setMode] = useState('add');
    const [editing, setEditing] = useState(null);
    const [visibleSubmissionModal, setVisibleSubmissionModal] = useState(false);
    const [submissionFormData, setSubmissionFormData] = useState(null);
    const [visibleCreateModal, setVisibleCreateModal] = useState(false);
    const [visiblePreviewModal, setVisiblePreviewModal] = useState(false);
    const [previewFormData, setPreviewFormData] = useState(null);

    const handleAdded = (newItem) => {
        setAssetForms(prev => [newItem, ...prev]);
        setTotal(prev => prev + 1);
        message.success('Form added successfully');
        setVisibleFormModal(false);             
    };

    const fetchForms = (pageArg = page) => {
        if (!assetId) return;
        assetFormService.getForm({ asset_id: assetId, page: pageArg }).then(res => {
            if (res.code === '0000') {
                setAssetForms(res.assetForms || []);
                setPage(pageArg);
                setTotal(res.total_count || 0);
            } else {
                message.error(res.message || 'Failed to fetch forms');
            }
        });
    };

    const handleDeleted = async (id) => {
        setAssetForms(prev => prev.filter(it => it.id !== id));
        setTotal(prev => Math.max(0, prev - 1));
    
        // if when delete last item on current page, go to previous page and refetch
        const willBeEmpty = (assetForms.length - 1) === 0 && page > 1;
        if (willBeEmpty) {
          const newPage = page - 1;
          setPage(newPage);
          fetchForms(newPage);
        }
        message.success('Form deleted successfully');
      };
    
    const handleEdited = (id, patch) => {
        setAssetForms(prev => prev.map(it => (it.id === id ? { ...it, ...patch } : it)));
        message.success('Form updated successfully');
    };

    const handlePageChange = (p) => {
        setPage(p);
    };

    const openAdd = () => { setMode('add'); setEditing(null); setVisibleFormModal(true); };
    const openPreviewForm = (record) => {
        if (!record?.form) return;
        setPreviewFormData({ ...record.form });
        setVisiblePreviewModal(true);
    };
    const closePreviewForm = () => {
        setVisiblePreviewModal(false);
        setPreviewFormData(null);
    };
    const openCreate = () => setVisibleCreateModal(true);
    const cancelCreate = () => setVisibleCreateModal(false);

    // Called by the form creator once a new form is saved. Only a Published
    // (public) form can be attached to an asset, so auto-attach when possible;
    // otherwise tell the user to publish it and use "Choose Form".
    const handleCreatedForm = (newForm) => {
        if (!newForm?.id) return;
        const isPublic = newForm.is_public === 1 || newForm.is_public === true;
        if (!isPublic) {
            message.info('Form created. Publish it, then use "Choose Form" to attach it to this asset.');
            return;
        }
        assetFormService.storeOrUpdate({ form_id: newForm.id, asset_id: assetId }).then(res => {
            if (res.code === '0000') {
                handleAdded(res.assetForm);
                return;
            }
            if (res.code === '0001' && Array.isArray(res.assetForm)) {
                message.error(res.assetForm.map(e => e.message).join(', '));
                return;
            }
            message.info('Form created, but it could not be attached automatically. Use "Choose Form" to attach it.');
        }).catch(() => {
            message.error('Form created, but attaching it to the asset failed.');
        });
    };
    const openSubmitForm = (record) => {
        const submissionUrl = record?.form?.public_form_url
            ? appendBackLink(record.form.public_form_url, 'manage-assets')
            : '';
        if (submissionUrl) {
            window.open(submissionUrl, '_blank', 'noopener,noreferrer');
            return;
        }
        message.warning('Form does not have a submission link yet.');
    };
    const openSubmissionList = (record) => {
        if (!record?.form) return;
        setSubmissionFormData({ ...record.form });
        setVisibleSubmissionModal(true);
    };
    const closeSubmissionList = () => {
        setVisibleSubmissionModal(false);
        setSubmissionFormData(null);
    };

    useEffect(() => {
        fetchForms();
    }, [assetId,page]);
  

    return (
    <>
      <Row gutter={12}>
        <Col xs={{ span: 12 }} md={{ span: 7 }}>
          <Button type="primary" key="choose" className="btn-create-breed" onClick={openAdd}>
            <IntlMessages id="propertyPage.mobModal.formTab.chooseButton" />
          </Button>
        </Col>
        <Col xs={{ span: 12 }} md={{ span: 7 }}>
          <Button type="primary" key="create" className="btn-create-breed" onClick={openCreate}>
            <IntlMessages id="propertyPage.mobModal.formTab.createButton" />
          </Button>
        </Col>
      </Row>

      <br />

      <AssignFormModal
        visibleAddForm={visibleFormModal}
        onCancelAddForm={() => setVisibleFormModal(false)}
        propertyId={propertyId}
        assetId={assetId}
        mode={mode}
        record={editing}
        onAdded={handleAdded}
        onEdited={handleEdited}
      />

      <CreateFormModal
        visibleFormModal={visibleCreateModal}
        cancelModal={cancelCreate}
        propertyId={propertyId}
        formData={{}}
        fromProperty
        updateFormData={() => {}}
        onCreated={handleCreatedForm}
      />

      <FormList
        forms={assetForms}
        page={page}
        pageSize={pageSize}
        total={total}
        fetchForms={fetchForms}
        onPageChange={handlePageChange}
        openPreview={openPreviewForm}
        onDeleted={(id) => handleDeleted(id)}
        openSubmit={openSubmitForm}
        openSubmission={openSubmissionList}
      />

      <PreviewFormModal
        visible={visiblePreviewModal}
        form={previewFormData}
        onCancel={closePreviewForm}
      />
      {submissionFormData && (
        <InspectionModal
          visibleInspectionModal={visibleSubmissionModal}
          cancelInspectionModal={closeSubmissionList}
          formId={submissionFormData?.id}
          propertyId={submissionFormData?.property_id || propertyId}
        />
      )}
    </>
  );
}
